import React from 'react';
import { Box, Card, CardContent, Typography, keyframes } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { InsightBullet, InsightSeverity } from './types';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SEVERITY_STYLES: Record<InsightSeverity, { bg: string; border: string; text: string }> = {
  success: { bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.35)', text: '#15803d' },
  warning: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.35)', text: '#b45309' },
  danger:  { bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.4)',  text: '#b91c1c' },
  info:    { bg: 'rgba(14,165,233,0.08)', border: 'rgba(14,165,233,0.35)', text: '#0369a1' },
};

const EASE_OUT: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

interface Props {
  bullets: InsightBullet[];
  loading: boolean;
  onLinkClick?: (linkTo: string) => void;
}

const SkeletonBar: React.FC = () => (
  <Card
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)',
      backgroundSize: '200% 100%',
      animation: `${shimmer} 1.5s infinite`,
      height: 72,
      mb: 3,
    }}
  />
);

const InsightsBar: React.FC<Props> = ({ bullets, loading, onLinkClick }) => {
  if (loading) return <SkeletonBar />;
  // Silêncio absoluto quando não há insights — não poluir o header com "nenhum alerta"
  if (!bullets.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE_OUT }}
    >
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          border: '1px solid rgba(120,105,223,0.15)',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        }}
      >
        <CardContent sx={{ py: '16px !important', px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: '#64748b',
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
              }}
            >
              Esta semana na sua rede
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <AnimatePresence>
              {bullets.map((bullet, idx) => {
                const styles = SEVERITY_STYLES[bullet.severity];
                const clickable = !!bullet.linkTo && !!onLinkClick;
                return (
                  <motion.div
                    key={bullet.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{ delay: idx * 0.05, duration: 0.25, ease: EASE_OUT }}
                  >
                    <Box
                      onClick={clickable && bullet.linkTo ? () => onLinkClick!(bullet.linkTo!) : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1,
                        borderRadius: 2,
                        border: `1px solid ${styles.border}`,
                        bgcolor: styles.bg,
                        cursor: clickable ? 'pointer' : 'default',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        '&:hover': clickable
                          ? { transform: 'translateX(2px)', boxShadow: `0 2px 8px ${styles.border}` }
                          : undefined,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.1rem', flexShrink: 0, lineHeight: 1 }}>
                        {bullet.icon}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: styles.text,
                          fontWeight: 500,
                          lineHeight: 1.35,
                          fontSize: '0.875rem',
                          flex: 1,
                        }}
                      >
                        {bullet.text}
                      </Typography>
                    </Box>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default InsightsBar;
